import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Color, DataItem, LegendPosition, ScaleType, Series } from '@swimlane/ngx-charts';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, takeUntil, tap } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { GroupService } from 'src/app/core/services/group.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { StateCacheService } from './../../../core/services/state-cache.service';
import { TokenUsageService } from './../../../core/services/token-usage.service';

@Component({
  selector: 'app-text-message-usage',
  templateUrl: './text-message-usage.component.html',
  styleUrls: ['./text-message-usage.component.scss']
})
export class TextMessageUsageComponent extends BaseComponent implements OnInit, OnDestroy {

  private usageSubject = new BehaviorSubject<any[]>([]);
  private historySubject = new BehaviorSubject<any[]>([]);

  filterOptions: any = {}
  usageFilterValue: string = 'week';
  maxDate: moment.Moment = moment();
  usageRecords?: any[];
  tokenHistory?: any[];

  view: [number, number] = [0, 0];
  showLegend: boolean = false;
  isMobile!: boolean
  legendPosition: LegendPosition = LegendPosition.Right;
  chartData: Series[] = [];
  barChartData: DataItem[] = [];
  barHeight: number = 400;
  gradient: boolean = false;
  showXAxis: boolean = true;
  showYAxis: boolean = true;
  showXAxisLabel: boolean = true;
  showYAxisLabel: boolean = true;
  xAxisLabel: string = 'Date';
  yAxisLabel: string = 'Total (Credits)';
  autoScale: boolean = true;
  timeline: boolean = true;

  colorScheme: Color = { domain: ['#009688', '#f44336'], group: ScaleType.Linear, selectable: false, name: 'Credits By Date', };
  barColorScheme: Color = {
    domain: ['#009688', '#00796B', '#004D40', '#26A69A', '#80CBC4'],
    group: ScaleType.Linear,
    selectable: true,
    name: 'Credit Usage By User'
  };


  creditBalance: number = 0;
  thisWeekTotal: number = 0;
  thisMonthTotal: number = 0;


  constructor(
    private groupService: GroupService,
    private usageService: TokenUsageService,
    private stateCache: StateCacheService,
    private breakpointObserver: BreakpointObserver,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe(result => {
        this.isMobile = result.matches;
        if (this.isMobile) {
          this.barHeight = 400;
        }
      });

    this.groupService.selectedGroup$.pipe(
      tap(selectedGroup => {
        if (selectedGroup) {
          this.setFilterOptions();
          this.loadUsage();
        }
      })
    ).subscribe();

    this.usageService.textMessageCreditBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.creditBalance = result;
      });

    this.usageService.textMessageGroupUsage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((usage: any[] | null) => {
        if (usage) {
          this.usageSubject.next(usage);
        }
      });

    this.usageService.groupTextMessageHistory$
      .pipe(takeUntil(this.destroy$))
      .subscribe((history: any[] | null) => {
        if (history) {
          this.historySubject.next(history);
        }
      });

    combineLatest([this.usageSubject, this.historySubject])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([usage, history]) => {
        if (this.usageSubject.getValue() && this.historySubject.getValue()) {
          this.usageRecords = this.usageSubject.getValue();
          this.tokenHistory = this.historySubject.getValue();
          this.prepareChartData();
          this.prepareBarChartData();
        }

      });

    this.usageService.weeklyTextMessageUsage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.thisWeekTotal = results;
      });

    this.usageService.monthlyTextMessageUsage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.thisMonthTotal = results;
      });
  }

  ngOnInit(): void {

    this.usageFilterValue = this.stateCache.get<string>('usageFilterValue') || 'week';
    setTimeout(() => {
      this.onUsageFilterSelection({ value: this.usageFilterValue });
    }, 500);

    // Load weekly and monthly totals on demand when component initializes
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedGroup => {
        if (selectedGroup) {
          this.usageService.getWeeklyTextMessageUsageByGroup(selectedGroup.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
          this.usageService.getMonthlyTextMessageUsageByGroup(selectedGroup.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        }
      });

  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateCache.set('exportFilterValue', this.usageFilterValue);
  }

  loadUsage() {
    this.usageService.resetLoadParameters()
    this.usageService.loadGroupTextMessageUsage(this.filterOptions.startDate, this.filterOptions.endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.usageService.loadGroupTextMessageHistory(this.filterOptions.startDate, this.filterOptions.endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private setFilterOptions() {
    const defaultEndDate = moment().utc();
    let startDate, endDate;


    this.usageFilterValue = 'week';
    startDate = moment.utc().subtract(7, 'days');
    endDate = defaultEndDate;

    // Ensure startDate is not greater than endDate
    while (startDate.isAfter(endDate, 'day')) {
      startDate = endDate.clone().subtract(1, 'day');
    }

    // Ensure dates are not in the future
    const today = moment.utc();
    if (startDate.isAfter(today, 'day')) {
      startDate = today;
    }
    if (endDate.isAfter(today, 'day')) {
      endDate = today;
    }

    this.filterOptions = { startDate, endDate };
  }


  onUsageFilterSelection(event: any) {

    let startDate;
    let endDate = moment();
    switch (event.value) {
      case 'week':
        startDate = moment().subtract(7, 'days');
        break;
      case 'month':
        startDate = moment().subtract(1, 'month');
        break;
      case 'all':
        startDate = moment().subtract(100, 'years');
        break;
      default:
        startDate = moment().subtract(100, 'years');
        break;
    }

    this.filterOptions = { startDate, endDate };
    this.loadUsage();
  }

  private prepareChartData() {
    if (!this.usageRecords || !this.tokenHistory) return;

    const aggregatedUsageData = this.usageRecords.reduce((acc: any, record: any) => {
      const date = moment(record.createdAt).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = 0;
      }
      acc[date] += parseFloat(record.creditsUsed || 0);
      return acc;
    }, {});

    const aggregatedHistoryData = this.tokenHistory.reduce((acc: any, record: any) => {
      const date = moment(record.createdAt).format('YYYY-MM-DD');
      const currentTimestamp = moment(record.createdAt).valueOf();
      if (!acc[date] || currentTimestamp > acc[date].timestamp) {
        acc[date] = { value: parseFloat(record.newBalance || 0), timestamp: currentTimestamp };
      }
      return acc;
    }, {});

    // Combine all dates from both datasets
    const allDates = new Set([
      ...Object.keys(aggregatedUsageData),
      ...Object.keys(aggregatedHistoryData),
    ]);

    this.chartData = [
      {
        name: 'Total Used (Credits)',
        series: Array.from(allDates).map(date => ({
          name: date,
          value: aggregatedUsageData[date] || 0, // Set value to 0 if date is missing
          min: 0
        })) as DataItem[]
      },
      {
        name: 'Balance (Credits)',
        series: Array.from(allDates).map(date => ({
          name: date,
          value: aggregatedHistoryData[date] ? aggregatedHistoryData[date].value : 0, // Handle missing dates
        })) as DataItem[]
      }
    ];

    // Sorting the series data using moment for accurate date comparison
    this.chartData[0].series.sort((a: any, b: any) => moment(a.name).diff(moment(b.name)));
    this.chartData[1].series.sort((a: any, b: any) => moment(a.name).diff(moment(b.name)));
  }

  private prepareBarChartData() {
    if (!this.usageRecords) return;

    const userAggregatedData = this.usageRecords.reduce((acc: any, record: any) => {
      const user = record.User;
      const key = `${user.title} ${user.name} ${user.surname}`;
      if (!acc[key]) {
        acc[key] = 0;
      }
      const amount = parseFloat(record.creditsUsed || 0);
      acc[key] += isNaN(amount) ? 0 : amount;
      return acc;
    }, {});

    this.barChartData = Object.keys(userAggregatedData).map(userName => ({
      name: userName,
      value: userAggregatedData[userName]
    }));
  }


}
